/**
 * MODULE: platform/browser/frontend/utils/browserSessionPersistence.ts
 * PURPOSE: Persist active browser session id across refresh (no unmount DELETE).
 * RECENT: 2026-06-17 [2.2.1434] — Expire stale sessionStorage entries before resume GET (avoids 404 noise).
 */

const SESSION_ID_KEY = 'brazn.browser.activeSessionId';
const SESSION_URL_KEY = 'brazn.browser.activeSessionUrl';
const SESSION_AT_KEY = 'brazn.browser.activeSessionAt';

/** Align with default BROWSER_SESSION_TIMEOUT_MINUTES (30) — skip resume GET when older. */
export const BROWSER_PERSISTED_SESSION_MAX_AGE_MS = 30 * 60 * 1000;

export type PersistedBrowserSession = {
  sessionId: string;
  currentUrl?: string;
};

export function persistBrowserSession(sessionId: string, currentUrl?: string): void {
  if (typeof sessionStorage === 'undefined' || !sessionId.trim()) return;
  sessionStorage.setItem(SESSION_ID_KEY, sessionId.trim());
  sessionStorage.setItem(SESSION_AT_KEY, String(Date.now()));
  const url = (currentUrl ?? '').trim();
  if (url) {
    sessionStorage.setItem(SESSION_URL_KEY, url);
  }
}

export function readPersistedBrowserSession(
  maxAgeMs = BROWSER_PERSISTED_SESSION_MAX_AGE_MS,
): PersistedBrowserSession | null {
  if (typeof sessionStorage === 'undefined') return null;
  const sessionId = sessionStorage.getItem(SESSION_ID_KEY)?.trim();
  if (!sessionId) return null;
  const atRaw = sessionStorage.getItem(SESSION_AT_KEY);
  if (atRaw) {
    const at = Number(atRaw);
    if (Number.isFinite(at) && maxAgeMs > 0 && Date.now() - at > maxAgeMs) {
      clearPersistedBrowserSession();
      return null;
    }
  }
  const currentUrl = sessionStorage.getItem(SESSION_URL_KEY)?.trim() || undefined;
  return { sessionId, currentUrl };
}

export function clearPersistedBrowserSession(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(SESSION_ID_KEY);
  sessionStorage.removeItem(SESSION_URL_KEY);
  sessionStorage.removeItem(SESSION_AT_KEY);
}

/** Test-only reset. */
export function resetBrowserSessionPersistenceForTests(): void {
  clearPersistedBrowserSession();
}
